import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { marketingCodesApi, type MarketingCodeDto, type CreateMarketingCodeDto, type UpdateMarketingCodeDto, type MarketingCodeStatsDto } from '../../services/adminApi';
import toast from 'react-hot-toast';
import { TagIcon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XMarkIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../../hooks/useConfirmationModal';

const AdminMarketingCodesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<MarketingCodeDto | null>(null);
  const [selectedStats, setSelectedStats] = useState<MarketingCodeStatsDto | null>(null);
  const [formData, setFormData] = useState<CreateMarketingCodeDto>({
    code: '',
    marketingPersonName: '',
    description: '',
    isActive: true,
    expiresAt: undefined
  });

  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  const { data: codes, isLoading, error } = useQuery({
    queryKey: ['marketing-codes'],
    queryFn: () => marketingCodesApi.getAll(),
  });

  const { data: stats } = useQuery({
    queryKey: ['marketing-codes-stats'],
    queryFn: () => marketingCodesApi.getStats(),
    enabled: isStatsModalOpen
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMarketingCodeDto) => marketingCodesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-codes'] });
      setIsCreateModalOpen(false);
      resetForm();
      toast.success('کد بازاریابی با موفقیت ایجاد شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در ایجاد کد بازاریابی');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMarketingCodeDto }) =>
      marketingCodesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-codes'] });
      setIsEditModalOpen(false);
      setSelectedCode(null);
      resetForm();
      toast.success('کد بازاریابی با موفقیت به‌روزرسانی شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی کد بازاریابی');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => marketingCodesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-codes'] });
      toast.success('کد بازاریابی با موفقیت حذف شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در حذف کد بازاریابی');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => marketingCodesApi.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-codes'] });
      toast.success('وضعیت کد بازاریابی به‌روزرسانی شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی وضعیت کد بازاریابی');
    },
  });

  const resetForm = () => {
    setFormData({
      code: '',
      marketingPersonName: '',
      description: '',
      isActive: true,
      expiresAt: undefined
    });
  };

  const handleCreate = () => {
    setIsCreateModalOpen(true);
    resetForm();
  };

  const handleEdit = (code: MarketingCodeDto) => {
    setSelectedCode(code);
    setFormData({
      code: code.code,
      marketingPersonName: code.marketingPersonName,
      description: code.description || '',
      isActive: code.isActive,
      expiresAt: code.expiresAt ? new Date(code.expiresAt).toISOString().split('T')[0] : undefined
    });
    setIsEditModalOpen(true);
  };

  const handleViewStats = async (code: MarketingCodeDto) => {
    try {
      const response = await marketingCodesApi.getStatsById(code.id);
      setSelectedStats(response.data);
      setSelectedCode(code);
      setIsStatsModalOpen(true);
    } catch (error: any) {
      toast.error('خطا در بارگذاری آمار');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditModalOpen && selectedCode) {
      updateMutation.mutate({ id: selectedCode.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    showModal(modalPresets.deleteEn('this marketing code', () => {
      deleteMutation.mutate(id);
    }));
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" dir="rtl">
        <p className="text-red-600">خطا در بارگذاری کدهای بازاریابی</p>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">کدهای بازاریابی</h1>
            <p className="text-gray-600 mt-2">مدیریت کدهای بازاریابی و ردیابی عملکرد آگهی‌ها</p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center gap-2 gap-2-reverse"
          >
            <PlusIcon className="h-5 w-5" />
            ایجاد کد
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TagIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {codes?.data?.length || 0}
              </h3>
              <p className="text-gray-600">کل کدها</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {codes?.data?.filter((c: MarketingCodeDto) => c.isActive).length || 0}
              </h3>
              <p className="text-gray-600">کدهای فعال</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {codes?.data?.reduce((sum: number, c: MarketingCodeDto) => sum + (c.totalAdsCount || 0), 0) || 0}
              </h3>
              <p className="text-gray-600">کل آگهی‌ها</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {codes?.data?.reduce((sum: number, c: MarketingCodeDto) => sum + (c.activeAdsCount || 0), 0) || 0}
              </h3>
              <p className="text-gray-600">آگهی‌های فعال</p>
            </div>
          </div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">کدهای بازاریابی</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">کد</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">بازاریاب</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">کل آگهی‌ها</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">آگهی‌های فعال</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ ایجاد</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {codes?.data?.map((code: MarketingCodeDto) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{code.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{code.marketingPersonName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{code.totalAdsCount || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{code.activeAdsCount || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      code.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {code.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(code.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <button
                        onClick={() => handleViewStats(code)}
                        className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                        title="مشاهده آمار"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(code)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                        title="ویرایش"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => toggleStatusMutation.mutate(code.id)}
                        className={`transition-colors duration-200 ${
                          code.isActive 
                            ? 'text-yellow-600 hover:text-yellow-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={code.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                      >
                        {code.isActive ? <XMarkIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        title="حذف"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {isEditModalOpen ? 'ویرایش کد بازاریابی' : 'ایجاد کد بازاریابی'}
                </h2>
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setIsEditModalOpen(false);
                    setSelectedCode(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  کد *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  maxLength={50}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="کد یکتا وارد کنید (مثال: MARKET2024)"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  نام بازاریاب *
                </label>
                <input
                  type="text"
                  value={formData.marketingPersonName}
                  onChange={(e) => setFormData({ ...formData, marketingPersonName: e.target.value })}
                  required
                  maxLength={200}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="نام بازاریاب را وارد کنید"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  توضیحات
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="توضیحات اختیاری"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    تاریخ انقضا (اختیاری)
                  </label>
                  <input
                    type="date"
                    value={formData.expiresAt || ''}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value || undefined })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center pt-8">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="mr-2 text-sm text-gray-700">فعال</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4 space-x-reverse pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setIsEditModalOpen(false);
                    setSelectedCode(null);
                    resetForm();
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  لغو
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-6 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEditModalOpen ? 'به‌روزرسانی' : 'ایجاد'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {isStatsModalOpen && selectedStats && selectedCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">
                  آمار: {selectedCode.code}
                </h2>
                <button
                  onClick={() => {
                    setIsStatsModalOpen(false);
                    setSelectedStats(null);
                    setSelectedCode(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">کد</label>
                  <p className="text-gray-900">{selectedCode.code}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">بازاریاب</label>
                  <p className="text-gray-900">{selectedCode.marketingPersonName}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">کل آگهی‌ها</label>
                  <p className="text-gray-900 text-2xl font-semibold">{selectedStats.totalAdsCount}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">آگهی‌های فعال</label>
                  <p className="text-gray-900 text-2xl font-semibold text-green-600">{selectedStats.activeAdsCount}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">آگهی‌های در انتظار</label>
                  <p className="text-gray-900 text-2xl font-semibold text-yellow-600">{selectedStats.pendingAdsCount}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">آگهی‌های فروخته شده</label>
                  <p className="text-gray-900 text-2xl font-semibold text-blue-600">{selectedStats.soldAdsCount}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">تاریخ ایجاد</label>
                  <p className="text-gray-900">{formatDate(selectedStats.createdAt)}</p>
                </div>
                {selectedStats.expiresAt && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">تاریخ انقضا</label>
                    <p className="text-gray-900">{formatDate(selectedStats.expiresAt)}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsStatsModalOpen(false);
                    setSelectedStats(null);
                    setSelectedCode(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  بستن
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'Confirm'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminMarketingCodesPage;



